import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { Adapt } from './lib/adapt.js';
import { VdjPost, VdjComfort, vdjColor, vdjBeatChroma, VDJ_PRESETS, VDJ_DROP_FLASH } from './lib/vdjLook.js';
import { SceneClock } from './lib/scene-clock.js';
import { FONT3x5 } from './splitflap2.js';

// Contrat du rig partagé : interdit. La salle a ses propres projecteurs.
export const RIG = 'forbidden';

export const PROFILE = {
  id: 'dancefloor',
  family: 'grid',
  danceability: 0.80,
  requiresTier: 0,
  energyLevel: 0.74,
  bpmMin: 100,
  bpmMax: 130,
  movement: 0.70,
  tonality: 0.60,
  enabled: true
};

// DANCEFLOOR : une piste de danse en dalles de verre lumineuses (16 x 12),
// vue en plongée depuis le bord, dans une boîte de nuit sombre. Disco, funk,
// house, pop, nu-disco. LOOK VDJ party (lib/vdjLook.js).
//
// LA SALLE EST ANALYTIQUE : un shader plein écran lance un rayon par pixel
// depuis une caméra privée (ses matrices passent en uniformes) et le coupe
// avec le sol, les murs et le plafond d'une boîte de 14 x 4,6 x 14,5 m.
// Sur la piste : cadre métallique (largeur en dérivées d'écran, AA 1 px),
// verre dépoli éclairé de l'intérieur (plus fort au centre, grain), reflet
// spéculaire des projecteurs du plafond, flaques de lumière des projecteurs,
// et les OMBRES des danseurs. Hors piste : sol sombre qui reçoit la lumière
// des dalles voisines ; murs sombres qui reflètent la couleur moyenne de la
// piste. Les faisceaux sont calculés par pixel (distance du rayon de vue à
// l'axe du cône, brume modulée), 4 au tier 1-2, 2 au tier 0.
//
// L'ÉTAT DE LA PISTE : deux textures 16 x 12 (précédente, courante) écrites
// par le CPU sur chaque temps (192 texels), fondues dans le shader en 0,1 s.
// Texel : R intensité, G index de couleur (palette de 4 + blanc), B 1 pour
// une dalle « lettre » (texte, plus brillante). Motifs : damier, vague, cible,
// spectre, pluie, serpent, étoile, texte (le nom de l'artiste en lettres de
// 3 x 5 dalles, la hauteur des lettres le long de la profondeur, défilant
// d'une colonne par croche).
//
// LES DANSEURS : 8 à 14 OMBRES projetées vers la caméra (corps, jambes qui
// alternent, bras qui se lèvent, tête), distance signée dans le plan du sol,
// pénombre douce ; cycles bouclés au BPM (balancement, pas, bras levés).
// (Les silhouettes de premier plan ont été retirées sur demande, 21/10/2026.)
//
// LA BOULE À FACETTES (hors champ, au-dessus de la piste) : N directions de
// Fibonacci tournées par une uniforme ; chaque point est un quad instancié
// que le vertex shader pose sur la paroi que son rayon touche (sol, murs,
// plafond), dans le plan de la paroi, taille ∝ distance : rien n'est
// téléversé par image.
//
// Le rythme : TEMPS = un pas du motif, projecteurs repositionnés, chroma
// vdj ; MESURE = nouveau motif ; PHRASE = palette, et le TEXTE une fois ;
// MONTÉE = damier qui alterne en croches, boule qui accélère ; DROP = toute
// la piste en blanc, puis la CIBLE en couleurs, flash (VdjComfort).
// Scène de 20 s (R20) : un motif fort et le nom de l'artiste au moins une fois.
//
// Debug : ?floorpattern=checker|wave|target|spectrum|rain|snake|star|text

const COLS = 16, ROWS = 12, TILE = 0.6;
const FX0 = -COLS * TILE / 2, FZ0 = 0.0;              // the floor's corner
const ROOM = { x: 7.0, y: 4.6, z0: -3.5, z1: 11.0 };
const BALL = new THREE.Vector3(0, 4.2, 3.8);
const PATTERNS = ['checker', 'wave', 'target', 'spectrum', 'rain', 'snake', 'star', 'text'];
const FALLBACK = [0xff2bd6, 0x2bd8ff, 0xffc21a, 0x8f2bff];
const SCENE_S = 20;
const MAX_DANCERS = 14, MAX_BEAMS = 4;
const SAFE_FLOOR = 0.4;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) { if (typeof location === 'undefined') return null; return new URLSearchParams(location.search).get(name); }
function mulberry(seed) { let a = seed >>> 0; return function () { a = (a + 0x6D2B79F5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }

const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const SAFE = `
  uniform vec2 uSafeCenter, uSafeHalfSize; uniform float uSafeFeather;
  float safeAtt(vec2 s) {
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;

// --- the room ----------------------------------------------------------------------------
const roomFragment = `
  precision highp float;
  ${SAFE}
  uniform mat4 uInvVP;
  uniform vec3 uCam;
  uniform vec2 uScreen;
  uniform float uAspect, uTime, uGain, uFlash, uMix, uBeat;
  uniform sampler2D uStateA, uStateB;
  uniform vec3 uPal[4];
  uniform vec3 uAvg;                 // the floor's mean light (walls, rims)
  uniform vec4 uDancer[${MAX_DANCERS}];   // x, z, phase (beats), style
  uniform float uDancerN, uBeats, uBeatPhase, uArmsUp;
  uniform vec3 uBeamS[${MAX_BEAMS}];
  uniform vec3 uBeamT[${MAX_BEAMS}];
  uniform vec4 uBeamC[${MAX_BEAMS}];      // colour, level
  uniform float uBeamN, uHaze;
  varying vec2 vUv;
  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  vec3 palette(float i) {
    if (i < 0.5) return uPal[0];
    if (i < 1.5) return uPal[1];
    if (i < 2.5) return uPal[2];
    if (i < 3.5) return uPal[3];
    return vec3(1.0);
  }
  vec4 tileState(vec2 cell) {
    vec2 uv = (cell + 0.5) / vec2(${COLS}.0, ${ROWS}.0);
    vec4 a = texture2D(uStateA, uv), b = texture2D(uStateB, uv);
    // intensity fades; the colour index switches at the middle of the fade
    return vec4(mix(a.r, b.r, uMix), uMix < 0.5 ? a.g : b.g, mix(a.b, b.b, uMix), 1.0);
  }
  vec3 tileLight(vec2 cell) {
    vec4 s = tileState(cell);
    return palette(floor(s.g * 255.0 / 32.0 + 0.5)) * s.r * (1.0 + 0.6 * s.b);
  }
  float sdSeg(vec2 p, vec2 a, vec2 b) { vec2 pa = p - a, ba = b - a; float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0); return length(pa - ba * h); }
  // the dancers' shadows on the floor plane (x, z): 0 lit .. 1 in full shadow
  float shadowAt(vec2 p) {
    float sh = 0.0;
    for (int k = 0; k < ${MAX_DANCERS}; k++) {
      if (float(k) >= uDancerN) break;
      vec4 d = uDancer[k];
      vec2 F = d.xy;
      vec2 D = normalize(vec2(F.x * 0.06, -1.0));          // cast toward the camera, fanning slightly
      vec2 Pp = vec2(-D.y, D.x);
      if (dot(p - (F + D * 0.8), p - (F + D * 0.8)) > 2.6) continue;
      float ph = uBeats + d.z;                               // in beats
      float style = d.w;
      float sway = sin(3.14159 * ph) * (style < 0.5 ? 0.16 : 0.07);
      float bounce = abs(sin(3.14159 * ph)) * 0.06;
      float step_ = sin(3.14159 * ph) * (style > 0.5 && style < 1.5 ? 0.14 : 0.05);
      vec2 hips = F + D * (0.42 + bounce) + Pp * sway * 0.4;
      vec2 shoulders = F + D * (1.22 + bounce) + Pp * sway;
      vec2 head = F + D * (1.48 + bounce) + Pp * sway * 1.1;
      float dist = sdSeg(p, hips, shoulders) - 0.14;
      dist = min(dist, length(p - head) - 0.12);
      dist = min(dist, sdSeg(p, F + Pp * 0.11 + D * step_, hips) - 0.07);
      dist = min(dist, sdSeg(p, F - Pp * 0.11 - D * step_, hips) - 0.07);
      // the arms: up on the beat for style 2 and in the builds, else swinging at the sides
      float up = max(style > 1.5 ? 0.5 + 0.5 * sin(3.14159 * 2.0 * ph) : 0.0, uArmsUp);
      vec2 handL = shoulders + D * (0.55 * up - 0.1) + Pp * (0.32 - 0.12 * up) + Pp * sway * 0.5;
      vec2 handR = shoulders + D * (0.55 * up - 0.1 + 0.08 * sin(6.2831853 * ph)) - Pp * (0.32 - 0.12 * up);
      dist = min(dist, sdSeg(p, shoulders + Pp * 0.14, handL) - 0.05);
      dist = min(dist, sdSeg(p, shoulders - Pp * 0.14, handR) - 0.05);
      // a soft penumbra, wider far from the feet (the light is broad)
      float pen = 0.05 + 0.06 * clamp(dot(p - F, D), 0.0, 1.6);
      sh = max(sh, 1.0 - smoothstep(-pen, pen, dist));
    }
    return sh;
  }
  // the projectors' pools of light on the floor
  vec3 pools(vec3 P) {
    vec3 c = vec3(0.0);
    for (int i = 0; i < ${MAX_BEAMS}; i++) {
      if (float(i) >= uBeamN) break;
      vec2 d = P.xz - uBeamT[i].xz;
      c += uBeamC[i].rgb * uBeamC[i].w * exp(-dot(d, d) / 0.55) * 0.55;
    }
    return c;
  }
  void main() {
    vec2 ndc = vUv * 2.0 - 1.0;
    vec4 a = uInvVP * vec4(ndc, -1.0, 1.0), b = uInvVP * vec4(ndc, 1.0, 1.0);
    vec3 O = uCam;
    vec3 R = normalize(b.xyz / b.w - a.xyz / a.w);
    // the room: floor, ceiling, side walls, back wall
    float tHit = 1e9; int which = -1;
    if (R.y < -1e-4) { float t = -O.y / R.y; if (t < tHit) { tHit = t; which = 0; } }
    if (R.y > 1e-4) { float t = (${ROOM.y.toFixed(1)} - O.y) / R.y; if (t < tHit) { tHit = t; which = 1; } }
    if (abs(R.x) > 1e-4) { float t = (sign(R.x) * ${ROOM.x.toFixed(1)} - O.x) / R.x; if (t > 0.0 && t < tHit) { tHit = t; which = 2; } }
    if (R.z > 1e-4) { float t = (${ROOM.z1.toFixed(1)} - O.z) / R.z; if (t < tHit) { tHit = t; which = 3; } }
    vec3 P = O + R * tHit;
    vec3 col = vec3(0.0);
    if (which == 0) {
      vec2 tu = vec2((P.x - ${FX0.toFixed(2)}) / ${TILE.toFixed(2)}, (P.z - ${FZ0.toFixed(2)}) / ${TILE.toFixed(2)});
      bool onFloor = tu.x >= 0.0 && tu.x < ${COLS}.0 && tu.y >= 0.0 && tu.y < ${ROWS}.0;
      float sh = shadowAt(P.xz);
      if (onFloor) {
        vec2 cell = floor(tu), f = fract(tu);
        vec2 fw = fwidth(tu) + 1e-5;
        // the metal frame: 7 % of a tile each side, edges anti-aliased over one pixel
        vec2 e = min(f, 1.0 - f);
        float frameD = min((e.x - 0.07) / fw.x, (e.y - 0.07) / fw.y);
        float glassM = smoothstep(-0.5, 0.5, frameD);
        vec3 L = tileLight(cell);
        // frosted glass lit from inside: brighter at the centre, a fine grain
        float centre = 1.0 - smoothstep(0.0, 0.62, length(f - 0.5));
        float grain = 0.93 + 0.07 * hash(floor(P.xz * 180.0));
        vec3 glass = (vec3(0.012) + L * (0.35 + 0.65 * centre) * grain) * (1.0 - 0.72 * sh);
        // the specular reflection of the ceiling lights (the projectors' heads) on the glass
        vec3 Rr = reflect(R, vec3(0.0, 1.0, 0.0));
        float spec = 0.0;
        for (int i = 0; i < ${MAX_BEAMS}; i++) { if (float(i) >= uBeamN) break; vec3 toS = normalize(uBeamS[i] - P); spec += pow(max(0.0, dot(Rr, toS)), 300.0) * uBeamC[i].w; }
        glass += vec3(1.0) * spec * 0.5 + (1.0 - 0.6 * sh) * pools(P) * 0.6;
        // the frame: brushed metal catching the tile's light and the ceiling
        vec3 metal = vec3(0.035) + L * 0.18 + vec3(0.6) * spec * 0.6 + pools(P) * 0.3;
        col = mix(metal * (1.0 - 0.6 * sh), glass, glassM);
      } else {
        // the dark floor around the piste, lit by the nearest tiles
        vec2 cc = clamp(floor(tu), vec2(0.0), vec2(${COLS - 1}.0, ${ROWS - 1}.0));
        float dTile = length(max(vec2(0.0), max(-tu, tu - vec2(${COLS}.0, ${ROWS}.0))));
        col = vec3(0.008) + tileLight(cc) * 0.25 * exp(-dTile * 1.6) + pools(P) * 0.5;
        col *= 1.0 - 0.6 * sh;
      }
    } else if (which == 1) {
      col = vec3(0.004) + uAvg * 0.03;
    } else {
      // the walls: dark, the floor's colour reflected low, fading with height
      float h = P.y;
      col = vec3(0.005) + uAvg * (0.12 * exp(-h * 1.3) + 0.01);
    }
    // the beams in the haze: distance from the view ray to each cone's axis
    for (int i = 0; i < ${MAX_BEAMS}; i++) {
      if (float(i) >= uBeamN) break;
      vec3 S = uBeamS[i], T = uBeamT[i];
      vec3 Bd = T - S; float bl = length(Bd); Bd /= bl;
      vec3 w0 = O - S;
      float bb = dot(R, Bd), d0 = dot(R, w0), e0 = dot(Bd, w0);
      float den = 1.0 - bb * bb;
      if (den < 1e-4) continue;
      float tr = (bb * e0 - d0) / den, tb = (e0 - bb * d0) / den;
      if (tr < 0.0 || tr > tHit || tb < 0.0 || tb > bl) continue;
      float dd = length((O + R * tr) - (S + Bd * tb));
      float wdt = 0.06 + 0.22 * tb / bl;
      float haze = 0.55 + 0.45 * sin(tb * 3.1 + uTime * 0.7 + float(i) * 2.0) * sin(tb * 1.3 - uTime * 0.4);
      col += uBeamC[i].rgb * uBeamC[i].w * exp(-(dd * dd) / (wdt * wdt)) * (0.3 + 0.7 * (1.0 - tb / bl)) * haze * uHaze * 0.32;
    }
    col = mix(col, vec3(1.0), uFlash);
    vec2 s = ndc * vec2(uAspect, 1.0);
    gl_FragColor = vec4(col * safeAtt(s) * uGain, 1.0);
  }
`;

// --- the mirror ball's spots: instanced quads laid on the room's surfaces -------------------
const spotsVertex = `
  precision highp float;
  attribute vec3 aDir;          // the facet's direction (unit, ball frame)
  attribute float aTint;        // 0 white, 1..4 palette
  uniform mat4 uVP;
  uniform float uAngle, uLevel;
  uniform vec3 uPal[4];
  uniform vec2 uSafeCenter, uSafeHalfSize; uniform float uSafeFeather;
  uniform float uAspect;
  varying vec2 vQ; varying vec3 vCol; varying float vSafe;
  void main() {
    float c = cos(uAngle), s = sin(uAngle);
    vec3 d = vec3(c * aDir.x + s * aDir.z, aDir.y, -s * aDir.x + c * aDir.z);
    vec3 B = vec3(${BALL.x.toFixed(2)}, ${BALL.y.toFixed(2)}, ${BALL.z.toFixed(2)});
    // the nearest wall the ray hits
    float t = 1e9; vec3 n = vec3(0.0, 1.0, 0.0);
    if (d.y < -1e-3) { float tt = -B.y / d.y; if (tt < t) { t = tt; n = vec3(0.0, 1.0, 0.0); } }
    if (d.y > 1e-3) { float tt = (${ROOM.y.toFixed(1)} - B.y) / d.y; if (tt < t) { t = tt; n = vec3(0.0, -1.0, 0.0); } }
    if (abs(d.x) > 1e-3) { float tt = (sign(d.x) * ${ROOM.x.toFixed(1)} - B.x) / d.x; if (tt < t) { t = tt; n = vec3(-sign(d.x), 0.0, 0.0); } }
    if (d.z > 1e-3) { float tt = (${ROOM.z1.toFixed(1)} - B.z) / d.z; if (tt < t) { t = tt; n = vec3(0.0, 0.0, -1.0); } }
    if (d.z < -1e-3) { float tt = (${ROOM.z0.toFixed(1)} - B.z) / d.z; if (tt < t) { t = tt; n = vec3(0.0, 0.0, 1.0); } }
    vec3 H = B + d * t + n * 0.004;
    vec3 t1 = normalize(abs(n.y) > 0.5 ? vec3(1.0, 0.0, 0.0) : cross(n, vec3(0.0, 1.0, 0.0)));
    vec3 t2 = cross(n, t1);
    float size = (0.006 + 0.004 * fract(aTint * 7.3 + aDir.x * 91.0)) * t / max(0.3, abs(dot(d, n)));      // the footprint grows with distance and incidence
    vec3 W = H + (t1 * (uv.x - 0.5) + t2 * (uv.y - 0.5)) * size * 2.0;
    gl_Position = uVP * vec4(W, 1.0);
    vQ = uv - 0.5;
    vec3 tint = aTint < 0.5 ? vec3(1.0, 0.97, 0.92) : (aTint < 1.5 ? uPal[0] : (aTint < 2.5 ? uPal[1] : (aTint < 3.5 ? uPal[2] : uPal[3])));
    float onWall = abs(n.y) < 0.5 ? 0.55 : 1.0;
    vCol = mix(tint, vec3(1.0), 0.4) * uLevel * 2.6 * onWall / (1.0 + 0.03 * t * t);
    vec4 cp = uVP * vec4(H, 1.0);
    vec2 sp = cp.xy / cp.w * vec2(uAspect, 1.0);
    vec2 q = abs(sp - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    vSafe = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));
  }
`;
const spotsFragment = `
  precision highp float;
  uniform float uGain;
  varying vec2 vQ; varying vec3 vCol; varying float vSafe;
  void main() {
    float r = length(vQ) * 2.0;
    float a = (1.0 - smoothstep(0.55, 1.0, r)) * 0.85 + (1.0 - smoothstep(0.0, 0.4, r)) * 0.4;
    gl_FragColor = vec4(vCol * a * vSafe * uGain, 1.0);
  }
`;

// --- the patterns (CPU, per beat): Float32 per tile [intensity, colour index, letter] ----------
function makePattern(kind, step, ctx) {
  const out = new Array(COLS * ROWS);
  const cx = (COLS - 1) / 2, cz = (ROWS - 1) / 2;
  for (let z = 0; z < ROWS; z++) for (let x = 0; x < COLS; x++) {
    let I = 0, c = 0, L = 0;
    const dx = x - cx, dz = z - cz, r = Math.hypot(dx, dz * 1.0);
    switch (kind) {
      case 'checker': { const on = ((x >> 1) + (z >> 1) + step) % 2 === 0; I = on ? 1 : 0.08; c = (step >> 1) % 4; break; }
      case 'wave': { const ph = (x - step * 2 + 64) % 8; I = Math.max(0.06, 1 - ph / 4); c = Math.floor((x + step) / 4) % 4; break; }
      case 'target': { const ring = Math.floor(r / 1.6); const on = (ring + step) % 3 === 0; I = on ? 1 : 0.1; c = (ring + step) % 4; break; }
      case 'spectrum': { const band = x < 5 ? ctx.bands.low : (x < 11 ? ctx.bands.mid : ctx.bands.high); const h = Math.round((0.2 + 0.8 * band * (0.7 + 0.3 * Math.sin(x * 1.7 + step))) * ROWS); I = z < h ? 0.9 : 0.06; c = z < h * 0.5 ? 0 : (z < h * 0.85 ? 1 : 2); break; }
      case 'rain': { const seed = (x * 7919) % 13; const head = (ROWS - 1) - ((step + seed) % (ROWS + 4)); const dd = z - head; I = dd >= 0 && dd < 4 ? 1 - dd * 0.25 : 0.05; c = x % 4; break; }
      case 'snake': { const path = ctx.snake; const k = path.indexOf(z * COLS + x); I = k >= 0 ? 1 - k / path.length * 0.8 : 0.06; c = k >= 0 ? Math.floor(k / 4) % 4 : 0; break; }
      case 'star': { const ang = Math.atan2(dz, dx); const ray = Math.abs(Math.sin(ang * 4 + step * 0.39)); I = ray < 0.18 + 0.05 * r ? 1 : 0.06; c = Math.floor(r / 2 + step) % 4; break; }
      case 'text': { const lit = ctx.text[z * COLS + x]; I = lit ? 1 : 0.05; c = lit ? 4 : 1; L = lit ? 1 : 0; break; }
      default: I = 0;
    }
    out[z * COLS + x] = [I, c, L];
  }
  return out;
}
// the artist's name in 3 x 5 letters, scrolled by `offset` columns; rows run along the depth (top of the letter = far)
function textGrid(str, offset) {
  const cells = new Uint8Array(COLS * ROWS);
  const s = (' ' + String(str || '').toUpperCase().replace(/[^A-Z0-9 :.\-]/g, '') + '   ');
  const width = s.length * 4;
  const z0 = 3;                                          // letters on rows 3..7
  for (let x = 0; x < COLS; x++) {
    const col = ((x + offset) % width + width) % width;
    const ch = s[Math.floor(col / 4)], cx = col % 4;
    if (cx === 3) continue;
    const bits = FONT3x5[ch] || FONT3x5[' '];
    for (let r = 0; r < 5; r++) if (bits[r * 3 + cx] === '1') cells[(z0 + 4 - r) * COLS + (COLS - 1 - x)] = 1;   // screen right is -x (the camera looks along +z)
  }
  return { cells, width };
}

export class DancefloorVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 2;
    this._nSpots = this._tier === 0 ? 300 : (this._tier === 1 ? 600 : 900);
    this._nBeams = this._tier === 0 ? 2 : 4;
    this._nDancers = this._tier === 0 ? 8 : 12;
    this._post = new VdjPost({ bloomStrength: 0.13, bloomThreshold: 0.78, grain: 0.015, vignette: 0.3 });
    this._comfort = new VdjComfort(VDJ_PRESETS.party.comfort);
    this._adapt = new Adapt();
    this._clock = new SceneClock({ period: SCENE_S, start: 0 });
    this._rng = mulberry(0xD15C0);
    const fp = urlParam('floorpattern'); this._forced = PATTERNS.includes(fp) ? fp : null;
    this._decisions = [];
  }
  get postProcessing() { return this._post.value; }
  _say(w) { this._decisions.push({ t: +this._time.toFixed(2), what: w }); if (this._decisions.length > 40) this._decisions.shift(); }

  init(scene) {
    this._scene = scene;
    this._time = 0; this._beats = 0; this._beatCount = 0; this._barCount = 0; this._step = 0; this._beatPeriod = 60 / 118;
    this._pattern = 'checker'; this._mixT = 1; this._dropAt = -100; this._whiteUntil = -1; this._flash = 0; this._lumSince = 0; this._textDone = false; this._textOffset = 0;
    this._ballAngle = 0; this._ballSpeed = 0.35; this._build = 0; this._artist = ''; this._artUrl = undefined; this._palIdx = 0;
    this._cam = new THREE.PerspectiveCamera(56, 16 / 9, 0.1, 60);
    this._size = new THREE.Vector2(); this._aspect = 0;
    const safe = { uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather };
    this._pal = FALLBACK.map((h) => vdjColor(h));
    const palU = { value: this._pal.map((c) => c.clone()) };
    // the floor states
    const mk = () => { const t = new THREE.DataTexture(new Uint8Array(COLS * ROWS * 4), COLS, ROWS, THREE.RGBAFormat); t.minFilter = t.magFilter = THREE.NearestFilter; t.generateMipmaps = false; t.needsUpdate = true; return t; };
    this._texA = mk(); this._texB = mk();
    this._dancers = [];
    const r = this._rng;
    for (let i = 0; i < MAX_DANCERS; i++) this._dancers.push({ x: (r() - 0.5) * COLS * TILE * 0.8, z: 1.2 + r() * (ROWS * TILE - 2.2), ph: r() * 2, style: Math.floor(r() * 3), w: 0.2 + r() * 0.3, wp: r() * 6.28 });
    this._u = Object.assign({
      uInvVP: { value: new THREE.Matrix4() }, uCam: { value: new THREE.Vector3() }, uScreen: { value: new THREE.Vector2(1920, 1080) }, uAspect: { value: 16 / 9 }, uTime: { value: 0 }, uGain: { value: 1 }, uFlash: { value: 0 },
      uMix: { value: 1 }, uBeat: { value: 0 }, uStateA: { value: this._texA }, uStateB: { value: this._texB }, uPal: palU, uAvg: { value: new THREE.Color(0.2, 0.1, 0.2) },
      uDancer: { value: Array.from({ length: MAX_DANCERS }, () => new THREE.Vector4()) }, uDancerN: { value: this._nDancers }, uBeats: { value: 0 }, uBeatPhase: { value: 0 }, uArmsUp: { value: 0 },
      uBeamS: { value: Array.from({ length: MAX_BEAMS }, () => new THREE.Vector3()) }, uBeamT: { value: Array.from({ length: MAX_BEAMS }, () => new THREE.Vector3()) }, uBeamC: { value: Array.from({ length: MAX_BEAMS }, () => new THREE.Vector4()) },
      uBeamN: { value: this._nBeams }, uHaze: { value: 1 }
    }, safe);
    this._room = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._u, vertexShader: quadVertex, fragmentShader: roomFragment, depthTest: false, depthWrite: false, extensions: { derivatives: true } }));
    this._room.frustumCulled = false; this._room.renderOrder = -1000;
    const self = this; this._room.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._room);
    // the ball's spots
    const N = this._nSpots, dirs = new Float32Array(N * 3), tints = new Float32Array(N), rr = mulberry(77);
    for (let i = 0; i < N; i++) {
      const y = 1 - 2 * (i + 0.5) / N + (rr() - 0.5) * 1.6 / N, rad = Math.sqrt(Math.max(0, 1 - y * y)), th = i * 2.399963 + (rr() - 0.5) * 0.9;
      dirs.set([Math.cos(th) * rad, y, Math.sin(th) * rad], i * 3);
      tints[i] = rr() < 0.25 ? 1 + Math.floor(rr() * 4) : 0;
    }
    const quad = new THREE.PlaneGeometry(1, 1); const g = new THREE.InstancedBufferGeometry();
    g.index = quad.index; g.setAttribute('position', quad.getAttribute('position')); g.setAttribute('uv', quad.getAttribute('uv'));
    g.setAttribute('aDir', new THREE.InstancedBufferAttribute(dirs, 3)); g.setAttribute('aTint', new THREE.InstancedBufferAttribute(tints, 1));
    g.instanceCount = N; g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);
    this._spotU = Object.assign({ uVP: { value: new THREE.Matrix4() }, uAngle: { value: 0 }, uLevel: { value: 0.6 }, uPal: palU, uAspect: this._u.uAspect, uGain: this._u.uGain }, safe);
    this._spots = new THREE.Mesh(g, new THREE.ShaderMaterial({ uniforms: this._spotU, vertexShader: spotsVertex, fragmentShader: spotsFragment, depthTest: false, depthWrite: false, transparent: true, blending: THREE.AdditiveBlending, side: THREE.DoubleSide }));
    this._spots.frustumCulled = false; this._spots.renderOrder = -900; scene.add(this._spots);
    this._beams = Array.from({ length: MAX_BEAMS }, (_, i) => ({ S: new THREE.Vector3((i % 2 ? 1 : -1) * 3.2, ROOM.y - 0.2, i < 2 ? 1.6 : 6.4), T: new THREE.Vector3(), T0: new THREE.Vector3(), T1: new THREE.Vector3((r() - 0.5) * 8, 0, r() * 7), at: 0 }));
    this._snake = this._makeSnake();
    this._writePattern(true);
  }

  _makeSnake() {
    // a closed loop over the floor (a rounded rectangle path), the snake slides along it
    const path = [];
    for (let x = 2; x < COLS - 2; x++) path.push(2 * COLS + x);
    for (let z = 2; z < ROWS - 2; z++) path.push(z * COLS + COLS - 3);
    for (let x = COLS - 3; x >= 2; x--) path.push((ROWS - 3) * COLS + x);
    for (let z = ROWS - 3; z >= 2; z--) path.push(z * COLS + 2);
    return path;
  }
  _writePattern(first) {
    // previous <- current, current <- the new step
    if (!first) { this._texA.image.data.set(this._texB.image.data); this._texA.needsUpdate = true; }
    const a = this._adapt.get();
    const bands = a.bands || { low: 0.5 + 0.3 * Math.sin(this._beats), mid: 0.45, high: 0.4 };
    let pat = this._pattern;
    const ctx = { bands, snake: [], text: null };
    if (pat === 'snake') { const L = this._snake.length, len = 14; for (let k = 0; k < len; k++) ctx.snake.push(this._snake[((this._step - k) % L + L) % L]); }
    if (pat === 'text') ctx.text = textGrid(this._artist || 'DANCE', this._textOffset).cells;
    let cells = makePattern(pat, this._step, ctx);
    if (this._time < this._whiteUntil) cells = cells.map(() => [1, 4, 0]);
    const d = this._texB.image.data;
    let sr = 0, sg = 0, sb = 0;
    for (let i = 0; i < COLS * ROWS; i++) {
      const [I, c, L] = cells[i];
      d[i * 4] = Math.round(clamp01(I) * 255); d[i * 4 + 1] = c * 32; d[i * 4 + 2] = L * 255; d[i * 4 + 3] = 255;
      const col = c === 4 ? { r: 1, g: 1, b: 1 } : this._pal[c]; sr += col.r * I; sg += col.g * I; sb += col.b * I;
    }
    if (first) { this._texA.image.data.set(d); this._texA.needsUpdate = true; }
    this._texB.needsUpdate = true;
    const n = COLS * ROWS; this._u.uAvg.value.setRGB(sr / n, sg / n, sb / n);
    this._mixT = 0;
  }
  _setPalette(profile) {
    const v = profile.artworkPalette && profile.artworkPalette.vividColors;
    const hexes = Array.isArray(v) && v.length >= 2 ? v : FALLBACK;
    for (let i = 0; i < 4; i++) { const h = hexes[(i + this._palIdx) % hexes.length]; this._pal[i] = vdjColor(typeof h === 'string' ? parseInt(h.replace('#', ''), 16) : h); this._u.uPal.value[i].copy(this._pal[i]); }
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== undefined && profile.playbackState !== 'PLAYING';
    const dt = paused ? 0 : Math.min(deltaTime, 0.05);
    this._clock.update(dt); this._adapt.update(dt, profile);
    if ((profile.artworkUrl || null) !== this._artUrl) { this._artUrl = profile.artworkUrl || null; this._setPalette(profile); }
    this._artist = String(profile.artist || profile.title || '').trim();
    if (paused) return;                                       // frozen, never black
    this._time += dt;
    const a = this._adapt.get();
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 118;
    this._beatPeriod = 60 / THREE.MathUtils.clamp(bpm, 60, 200);
    const bp = a.beatPhase != null ? a.beatPhase : 0;
    this._beats += dt / this._beatPeriod;
    const director = profile.director || {};
    const ev = director.nextEvent;
    const dropMs = ev && ev.type === 'drop' && ev.msUntil != null ? ev.msUntil : null;
    const beatsToDrop = dropMs != null && dropMs > 0 ? dropMs / 1000 / this._beatPeriod : null;
    const buildT = beatsToDrop != null && beatsToDrop < 16 ? clamp01((16 - beatsToDrop) / 14) : (director.mode === 'build' ? 0.4 : 0);
    this._build += (buildT - this._build) * Math.min(1, dt / 0.3);
    // the eighth notes (the build's checker alternates on them, the text scrolls on them)
    const eighth = Math.floor(this._beats * 2);
    const eighthEdge = eighth !== this._lastEighth; this._lastEighth = eighth;
    let drop = a.drop;
    // the scene (R20): without a drop, a strong moment at 14 s
    if (!a.dropReady && this._clock.t >= 14 && !this._peakDone) { drop = true; this._peakDone = true; }
    if (this._clock.wrapped) { this._peakDone = false; this._textDone = false; }
    if (a.bar) {
      this._barCount++;
      if (!this._forced && this._time - this._dropAt > this._beatPeriod * 8) {
        // the name once a phrase (and at least once a 20 s scene)
        if (!this._textDone && (this._clock.t > 4 || a.phrase)) { this._pattern = 'text'; this._textDone = true; this._textOffset = 0; }
        else { const pool = PATTERNS.filter((p) => p !== 'text' && p !== this._pattern); this._pattern = pool[Math.floor(this._rng() * pool.length)]; }
        this._say('motif ' + this._pattern);
      }
    }
    if (this._forced) this._pattern = this._forced;
    if (a.phrase) { this._palIdx++; this._setPalette(profile); this._textDone = false; this._say('phrase : palette'); }
    if (drop && this._time - this._dropAt > this._beatPeriod * 8) {
      this._dropAt = this._time; this._whiteUntil = this._time + this._beatPeriod * 0.9; this._pattern = this._forced || 'target';
      if (this._comfort.requestFlash(this._time)) this._flash = VDJ_DROP_FLASH * 0.55;
      this._step++; this._writePattern(false); this._say('DROP');
    }
    const buildChecker = !this._forced && this._build > 0.6;
    if (buildChecker) this._pattern = 'checker';
    if (a.beat) {
      this._beatCount++;
      // the beams: new targets on the beat
      for (const b of this._beams) { b.T0.copy(b.T); b.T1.set((this._rng() - 0.5) * COLS * TILE * 0.9, 0, 0.4 + this._rng() * (ROWS * TILE - 0.8)); b.at = this._time; }
    }
    if ((a.beat && !(buildChecker || this._pattern === 'text')) || (eighthEdge && (buildChecker || this._pattern === 'text'))) {
      this._step++; if (this._pattern === 'text') this._textOffset++;
      this._writePattern(false);
    }
    // uniforms
    this._mixT = Math.min(1, this._mixT + dt / 0.1);
    this._u.uMix.value = this._mixT * this._mixT * (3 - 2 * this._mixT);
    this._u.uTime.value = this._time; this._u.uBeats.value = this._beats; this._u.uBeatPhase.value = bp;
    this._u.uArmsUp.value = Math.max(this._build, this._time - this._dropAt < this._beatPeriod * 8 ? 1 - (this._time - this._dropAt) / (this._beatPeriod * 8) : 0);
    for (let i = 0; i < this._nDancers; i++) {
      const d = this._dancers[i];
      // wandering slowly on small loops, on the floor
      const x = d.x + d.w * Math.sin(this._time * 0.13 + d.wp), z = d.z + d.w * Math.cos(this._time * 0.11 + d.wp);
      this._u.uDancer.value[i].set(x, z, d.ph, d.style);
    }
    for (let i = 0; i < MAX_BEAMS; i++) {
      const b = this._beams[i];
      const k = clamp01((this._time - b.at) / (this._beatPeriod * 0.6)); const e = k * k * (3 - 2 * k);
      b.T.lerpVectors(b.T0, b.T1, e);
      this._u.uBeamS.value[i].copy(b.S); this._u.uBeamT.value[i].copy(b.T);
      const c = this._pal[(i + this._beatCount) % 4];
      const lvl = (0.6 + 0.5 * a.intensity) * (0.75 + 0.25 * Math.pow(1 - bp, 2));
      this._u.uBeamC.value[i].set(c.r, c.g, c.b, lvl);
    }
    this._u.uHaze.value = 0.7 + 0.5 * a.intensity;
    // the ball: faster in the builds
    this._ballSpeed += ((0.35 + 1.4 * this._build) - this._ballSpeed) * Math.min(1, dt / 1.0);
    this._ballAngle += this._ballSpeed * dt;
    this._spotU.uAngle.value = this._ballAngle; this._spotU.uLevel.value = 0.55 + 0.25 * a.intensity;
    this._u.uFlash.value = this._flash; this._flash = Math.max(0, this._flash - dt / 0.12);
    // the camera: a slow drift, a small kick on the beat
    const kick = 0.04 * Math.pow(1 - bp, 3) * Math.max(0.3, a.pulse);
    this._cam.position.set(0.35 * Math.sin(this._time * 0.09), 2.6 + kick, -2.4);
    this._cam.lookAt(0.15 * Math.sin(this._time * 0.07), 0, 3.8);
    // R10
    const avg = this._u.uAvg.value; const est = 0.05 + 0.35 * (avg.r * 0.2126 + avg.g * 0.7152 + avg.b * 0.0722) + 0.6 * this._u.uFlash.value;
    this._lumSince += dt; if (this._lumSince > 0.25) { this._u.uGain.value = this._comfort.measure(est, this._lumSince); this._lumSince = 0; }
    this._post.update(vdjBeatChroma(bp, this._beatPeriod) * 0.6 + 0.3);
  }

  _render(renderer) {
    renderer.getDrawingBufferSize(this._size);
    const prev = renderer.getRenderTarget();
    let W = this._size.x, H = this._size.y; if (prev) { W = prev.width; H = prev.height; }
    const aspect = W / Math.max(1, H);
    this._u.uScreen.value.set(W, H); this._u.uAspect.value = aspect;
    if (Math.abs(aspect - this._cam.aspect) > 1e-4) { this._cam.aspect = aspect; this._cam.updateProjectionMatrix(); }
    this._cam.updateMatrixWorld();
    const vp = new THREE.Matrix4().multiplyMatrices(this._cam.projectionMatrix, this._cam.matrixWorldInverse);
    this._spotU.uVP.value.copy(vp);
    this._u.uInvVP.value.copy(vp).invert();
    this._u.uCam.value.copy(this._cam.position);
  }

  get probe() {
    return { tier: this._tier, pattern: this._pattern, step: this._step, spots: this._nSpots, beams: this._nBeams, dancers: this._nDancers, build: +this._build.toFixed(2), ball: +this._ballSpeed.toFixed(2), gain: +this._u.uGain.value.toFixed(2), scene: +this._clock.t.toFixed(2), artist: this._artist, decisions: this._decisions.slice(-8) };
  }

  dispose() {
    for (const m of [this._room, this._spots]) if (m) { if (this._scene) this._scene.remove(m); m.geometry.dispose(); m.material.dispose(); }
    if (this._room) this._room.onBeforeRender = null;
    if (this._texA) this._texA.dispose(); if (this._texB) this._texB.dispose();
    this._scene = null;
  }
}
